import React, { CSSProperties, useEffect, useState } from "react";
import LoaderSpinner from "react-loader-spinner";
import { useSelector } from "react-redux";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import PoweredBy from "./PoweredBy/PoweredBy";
import { useTimerContext } from "../hooks/use-timer-context";
import transferLoaderStyles from "./Loader.module.scss";
import pendingSvg from "../assets/images/pending.svg";

const BankLoader = ({ message }: { message?: string }) => {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.5)", // Dimmed background
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000, // Ensure it overlays everything
        color: "#fff",
        fontSize: "16px",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: "auto",
          width: "auto",
          minHeight: "350px",
          minWidth: "400px",
          background: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
        }}
      >
        {/* Spinner */}
        <div
          style={{
            position: "relative",
            width: "60px",
            height: "60px",
            marginBottom: "16px", // Space below spinner
          }}
        >
          {/* Outer Spinner */}
          <div
            style={{
              border: "6px solid #AAAAAA",
              borderTop: "6px solid #8E173E", // Purple color
              borderRadius: "50%",
              width: "100%",
              height: "100%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
        </div>

        {/* Message */}
        {message && (
          <p
            style={{
              marginTop: "16px",
              fontWeight: "500",
              fontSize: "23px",
              color: "#333",
            }}
          >
            {message}
          </p>
        )}
        <p style={{ fontSize: "16px", color: "#555" }}>This may take a while</p>

        {/* Placeholder for PoweredBy */}
        <PoweredBy />
      </div>

      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

const TransferLoader = ({
  message = "Checking Transaction Status",
  initialTime = 120, // Default to 2 minutes (120 seconds)
}: {
  message?: string;
  initialTime?: number; // Timer duration in seconds
}) => {
  const {
    pauseTimer,
    isTimerPaused,
    timeRemainingInMinutesText,
    timeRemainingInSeconds,
  } = useTimerContext();
  const [didUserContinueWait, setDidUserContinueWait] = useState(false); // checks if the use continued to wait when the minutes left reached the set threshold
  const MINUTE_TO_SHOW_CLOSE_PROMPT = 5; // shows the prompt for use to close timer at the 5 minute mark
  const MINUTE_TO_SECOND_CONVERTER = 60; //

  const timeLeftInMinutes = timeRemainingInSeconds / MINUTE_TO_SECOND_CONVERTER;

  const handleWaitClick = () => {
    setDidUserContinueWait(true);
  };

  const handleClose = () => {
    // history.push("/"); // Redirect to home page
    window.location.reload(); //refresh page
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.5)", // Dimmed background
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        color: "#fff",
        fontSize: "16px",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: "auto",
          width: "auto",
          minHeight: "350px",
          minWidth: "400px",
          background: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
        }}
      >
        {isTimerPaused || timeRemainingInSeconds === 0 ? (
          <>
            <img
              src={pendingSvg}
              alt="Pending"
              className={transferLoaderStyles.pendingImg}
            />
            <p
              style={{
                fontSize: "14px",
                color: "#555",
                maxWidth: "280px",
                marginInline: "auto",
                textAlign: "center",
              }}
            >
              A mail will be sent to you once your transaction is confirmed.
            </p>
            <div className={transferLoaderStyles.loaderActions}>
              <button
                className={`${transferLoaderStyles.solid}`}
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            {" "}
            {/* Spinner */}
            <div
              style={{
                position: "relative",
                width: "60px",
                height: "60px",
                marginBottom: "16px",
              }}
            >
              {/* Circular spinner */}

              <div
                style={{
                  border: "6px solid #AAAAAA",
                  borderTop: "6px solid #8E173E",
                  borderRadius: "50%",
                  width: "100%",
                  height: "100%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            {/* Main Message */}
            <p style={{ fontWeight: "500", fontSize: "23px", color: "#333" }}>
              {message}
            </p>
            {timeLeftInMinutes < MINUTE_TO_SHOW_CLOSE_PROMPT &&
            !didUserContinueWait ? (
              <>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    maxWidth: "280px",
                    marginInline: "auto",
                    textAlign: "center",
                  }}
                >
                  This is taking longer than expected. Do you want to get
                  notified when your transaction is completed or continue
                  waiting?
                </p>
                <div className={`${transferLoaderStyles.loaderActions}`}>
                  <button
                    className={`${transferLoaderStyles.outlined}`}
                    onClick={handleWaitClick}
                  >
                    Wait
                  </button>
                  <button
                    className={`${transferLoaderStyles.solid}`}
                    onClick={pauseTimer}
                  >
                    Notify me
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Countdown Timer */}
                <p style={{ fontSize: "16px", color: "#555" }}>
                  This may take up to{" "}
                  <span style={{ color: "#8E173E" }}>
                    {timeRemainingInMinutesText}
                  </span>{" "}
                  minutes
                </p>
              </>
            )}
            {/* Powered By Placeholder */}
            <PoweredBy />
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  loaderContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1400,
  } as CSSProperties,
};
export default function Loader() {
  const LoaderSelector = useSelector((state) => state.loader);
  const { LoaderOpened, loaderType, message } = LoaderSelector;
  const { timeRemainingInSeconds } = useTimerContext();

  if (!LoaderOpened) return null;

  return (
    <>
      {loaderType === "bank" && (
        <BankLoader
          message={message || "Processing your bank transaction..."}
        />
      )}
      {loaderType === "transfer" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "opay" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "crypto" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "palmpay" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "momo" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "MOMO-MALI" && (
        <TransferLoader
          message={message || "Checking Transaction Status"}
          initialTime={
            timeRemainingInSeconds && timeRemainingInSeconds > 0
              ? timeRemainingInSeconds
              : 900
          } // Can be dynamically passed
        />
      )}
      {loaderType === "default" && (
        <div style={styles.loaderContainer}>
          <LoaderSpinner type="Oval" color="#8E173E" height={50} width={50} />
        </div>
      )}
    </>
  );
}
